'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useAuth } from '@/contexts/AuthContext';
import NotificationBell from '@/components/NotificationBell';

export type ActivePage = 'home' | 'clips' | 'news' | 'exclusive' | 'other' | 'blog';

interface NavBarProps {
  /** Which nav item to highlight as active */
  activePage?: ActivePage;
  /** Extra right-side content (search, submit button, etc.) */
  rightSlot?: React.ReactNode;
  /** Extra items appended inside the mobile menu, below the network links */
  mobileMenuExtra?: React.ReactNode;
  /** Called when the mobile menu opens/closes — useful for pages that need to know */
  onMobileMenuChange?: (open: boolean) => void;
  /** When true the header sticks below the news ticker (top-8); otherwise top-0 */
  hasTicker?: boolean;
}

const NAV_LINKS = [
  { href: '/', label: 'ViddStarTV', page: 'home' as ActivePage, emoji: '📺' },
  { href: '/clips', label: 'ViddStar Clips', page: 'clips' as ActivePage, emoji: '⚡' },
  { href: '/news', label: 'ViddStar News', page: 'news' as ActivePage, emoji: '📰' },
  { href: '/blog', label: 'Blog', page: 'blog' as ActivePage, emoji: '✍️' },
  { href: '/exclusive', label: 'ViddStar Exclusive', page: 'exclusive' as ActivePage, emoji: '★', star: true },
];

const CREATOR_LINK = { href: '/creator', label: 'Creator', emoji: '🎬' };
const DASHBOARD_LINK = { href: '/creator/dashboard', label: 'Dashboard', emoji: '📊' };

export default function NavBar({
  activePage = 'other',
  rightSlot,
  mobileMenuExtra,
  onMobileMenuChange,
  hasTicker = false,
}: NavBarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { user, profile, loading } = useAuth();

  const toggleMenu = () => {
    const next = !mobileMenuOpen;
    setMobileMenuOpen(next);
    onMobileMenuChange?.(next);
  };

  const closeMenu = () => {
    setMobileMenuOpen(false);
    onMobileMenuChange?.(false);
  };

  const stickyClass = hasTicker ? 'sticky top-8 z-40' : 'sticky top-0 z-40';

  const AuthButtons = () => {
    if (loading) return null;
    if (user) {
      return (
        <div className="flex items-center gap-1">
          <NotificationBell />
          <Link
            href="/viewer"
            className="flex items-center gap-1.5 text-sm font-bold uppercase tracking-wide px-3 py-1.5 rounded-[4px] text-text-secondary hover:text-text-primary hover:bg-surface-tertiary transition-colors duration-150"
          >
            <span className="w-6 h-6 rounded-full bg-brand-red flex items-center justify-center text-white text-xs font-bold uppercase">
              {(profile?.full_name || user.email || 'V')[0]}
            </span>
            <span className="hidden lg:inline">My Account</span>
          </Link>
        </div>
      );
    }
    return null;
  };

  return (
    <header className={`flex flex-col w-full bg-surface-secondary border-b border-stroke-primary ${stickyClass}`}>
      {/* ── Main row ── */}
      <div className="flex items-center justify-between px-4 md:px-6 h-[60px] md:h-[70px]">
        {/* Logo + desktop nav */}
        <div className="flex items-center gap-4 md:gap-6 min-w-0">
          <Link href="/" className="flex items-center gap-2 flex-shrink-0" aria-label="ViddStar Home">
            <img
              src="/assets/images/Untitled_design_-_2026-04-29T224248.327-1777517026864.png"
              alt="ViddStar Logo"
              className="h-12 md:h-16 w-auto object-contain"
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1" aria-label="Main navigation">
            {NAV_LINKS.map(({ href, label, page, star }) => {
              const isActive = activePage === page;
              return (
                <Link
                  key={href}
                  href={href}
                  className={`text-sm font-bold uppercase tracking-wide transition-colors duration-150 px-3 py-1.5 rounded-[4px] flex items-center gap-1 ${
                    isActive
                      ? 'text-white bg-brand-red' :'text-text-secondary hover:text-text-primary hover:bg-surface-tertiary'
                  }`}
                >
                  {star && <span className={isActive ? 'text-white' : 'text-brand-red'}>★</span>}
                  {label}
                </Link>
              );
            })}
            <Link
              href={CREATOR_LINK.href}
              className="text-sm font-bold uppercase tracking-wide transition-colors duration-150 px-3 py-1.5 rounded-[4px] flex items-center gap-1 text-text-secondary hover:text-text-primary hover:bg-surface-tertiary"
            >
              {CREATOR_LINK.emoji} {CREATOR_LINK.label}
            </Link>
            <Link
              href={DASHBOARD_LINK.href}
              className="text-sm font-bold uppercase tracking-wide transition-colors duration-150 px-3 py-1.5 rounded-[4px] flex items-center gap-1 text-text-secondary hover:text-text-primary hover:bg-surface-tertiary"
            >
              {DASHBOARD_LINK.emoji} {DASHBOARD_LINK.label}
            </Link>
          </nav>
        </div>

        {/* Right slot + auth + hamburger */}
        <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
          {rightSlot}
          <AuthButtons />

          {/* Hamburger — always visible on mobile */}
          <button
            onClick={toggleMenu}
            className="md:hidden flex items-center justify-center w-10 h-10 text-text-secondary hover:text-text-primary hover:bg-surface-tertiary rounded-[4px] transition-colors"
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen
                ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              }
            </svg>
          </button>
        </div>
      </div>

      {/* ── Mobile dropdown ── */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-stroke-primary bg-surface-secondary">
          <nav className="flex flex-col px-4 py-3 gap-0.5" aria-label="Mobile navigation">
            <p className="text-text-muted text-xs uppercase tracking-widest mb-2 pt-1">ViddStar Network</p>
            {NAV_LINKS.map(({ href, label, page, emoji }) => {
              const isActive = activePage === page;
              return (
                <Link
                  key={href}
                  href={href}
                  onClick={closeMenu}
                  className={`flex items-center gap-2 text-sm font-bold uppercase tracking-wide py-3 px-3 rounded-[4px] min-h-[44px] transition-colors duration-150 ${
                    isActive
                      ? 'text-white bg-brand-red' :'text-text-secondary hover:text-text-primary hover:bg-surface-tertiary'
                  }`}
                >
                  <span>{emoji}</span>
                  {label}
                </Link>
              );
            })}
            <Link
              href={CREATOR_LINK.href}
              onClick={closeMenu}
              className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide py-3 px-3 rounded-[4px] min-h-[44px] transition-colors duration-150 text-text-secondary hover:text-text-primary hover:bg-surface-tertiary"
            >
              <span>{CREATOR_LINK.emoji}</span>
              {CREATOR_LINK.label}
            </Link>
            <Link
              href={DASHBOARD_LINK.href}
              onClick={closeMenu}
              className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide py-3 px-3 rounded-[4px] min-h-[44px] transition-colors duration-150 text-text-secondary hover:text-text-primary hover:bg-surface-tertiary"
            >
              <span>{DASHBOARD_LINK.emoji}</span>
              {DASHBOARD_LINK.label}
            </Link>

            {/* Auth links in mobile menu */}
            {!loading && user && (
                <Link
                  href="/viewer"
                  onClick={closeMenu}
                  className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide py-3 px-3 rounded-[4px] min-h-[44px] transition-colors duration-150 text-text-secondary hover:text-text-primary hover:bg-surface-tertiary"
                >
                  <span>👤</span>
                  My Account
                </Link>
            )}

            {/* Extra items injected by individual pages */}
            {mobileMenuExtra && (
              <div className="mt-2 pt-2 border-t border-stroke-primary">
                {mobileMenuExtra}
              </div>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
